import React, { useCallback, useMemo, useRef, useState } from "react";
import ToolPrepPopup from "../../../shared/Popup/ToolPrepPopup";
import ToolPrepSourceCards from "../../../shared/ToolPrepSourceCards";
import { useSyncScrollbarWidth } from "../../../hooks/useSyncScrollbarWidth";
import DocumentFileSourceCard from "../../shared/DocumentFileSourceCard";
import DocumentPageThumbnails from "../../shared/DocumentPageThumbnails";
import { useDocumentFileSource } from "../../shared/useDocumentFileSource";
import { PDF_WATERMARK_MAX_PAGES } from "../../shared/renderPdfFirstPage";
import WatermarkPreview from "../../../watermark/Popup/WatermarkPreview";
import WatermarkTextFormatToolbar from "../../../watermark/Popup/WatermarkTextFormatToolbar";
import WatermarkLeftPanelOverlays from "../../../watermark/Popup/WatermarkLeftPanelOverlays";
import { WatermarkToolbarOverlayProvider } from "../../../watermark/Popup/WatermarkToolbarOverlayContext";
import {
  DEFAULT_PAGE_NUMBER_SETTINGS,
  pageNumberSettingsToWatermarkPreview,
  watermarkPatchToPageNumberSettings,
} from "./pageNumberSettings";
import { downloadPageNumbersPdf } from "./bakePageNumbersPdf";
import PageNumberControls from "./PageNumberControls";
import { TOOL_PREP_PREVIEW_BG, TOOL_PREP_PREPARING_PREVIEW_LABEL } from "../../../../../utils/editorEntrySession";
import {
  DOCUMENT_PREP_STAGE_INSET,
  TOOL_PREP_STAGE_INSET,
} from "../../../lib/toolPrepImageLayout";
import "./PdfPageNumbersToolPrep.css";

const HINT =
  "Adjust format and position on the preview, then download the numbered PDF.";

/**
 * PDF Page Numbers prep — upload, live preview, bake numbers into download.
 */
const PdfPageNumbersToolPrep = ({ onCancel = () => {}, onDone = null }) => {
  const scrollBodyRef = useRef(null);
  const [pageNumbers, setPageNumbers] = useState(DEFAULT_PAGE_NUMBER_SETTINGS);
  const [compareMode, setCompareMode] = useState("preview");
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(null);
  const [exportError, setExportError] = useState(null);
  const [exportSuccess, setExportSuccess] = useState(null);

  const {
    inputRef,
    accept,
    file,
    pdfFile,
    fileName,
    fileSizeLabel,
    previewUrl,
    pageUrls,
    pageCount,
    currentPage,
    loadingPreview,
    loadingPage,
    error,
    hasFile,
    clear,
    openPicker,
    onInputChange,
    onDropFiles,
    ensurePageUrl,
    goToPage,
    uploadProgress,
  } = useDocumentFileSource({ acceptKind: "pdf", busy: exporting });

  useSyncScrollbarWidth(scrollBodyRef, [
    previewUrl,
    hasFile,
    pageCount,
    currentPage,
    pageNumbers,
  ]);

  const previewWatermark = useMemo(
    () =>
      pageNumberSettingsToWatermarkPreview(
        pageNumbers,
        currentPage || 1,
        pageCount || 1
      ),
    [pageNumbers, currentPage, pageCount]
  );

  const mergeFromWatermark = useCallback((wm) => {
    setPageNumbers((prev) => watermarkPatchToPageNumberSettings(wm, prev));
  }, []);

  const handleClear = () => {
    if (exporting) return;
    clear();
    setPageNumbers(DEFAULT_PAGE_NUMBER_SETTINGS);
    setCompareMode("preview");
    setExportError(null);
    setExportProgress(null);
    setExportSuccess(null);
  };

  const handleDone = async () => {
    const bakeSource = pdfFile || file;
    if (!bakeSource || !previewUrl || exporting) return;

    setExporting(true);
    setExportError(null);
    setExportSuccess(null);
    setExportProgress({
      page: 0,
      total: Math.min(pageCount || 1, PDF_WATERMARK_MAX_PAGES),
    });

    try {
      await new Promise((r) => setTimeout(r, 0));
      const result = await downloadPageNumbersPdf(bakeSource, pageNumbers, {
        onProgress: setExportProgress,
      });
      setExportSuccess(result.fileName || "PDF downloaded");
      if (typeof onDone === "function") {
        onDone({
          mode: "pdf_page_numbers",
          exported: true,
          stayOpen: true,
          fileName: result.fileName,
          pageCount: result.pageCount,
        });
      }
    } catch (err) {
      setExportError(err?.message || "Could not export numbered PDF.");
    } finally {
      setExporting(false);
      setExportProgress(null);
    }
  };

  const progressHint =
    exporting && exportProgress
      ? `Baking page ${exportProgress.page || 1} of ${exportProgress.total}…`
      : exportError
        ? exportError
        : exportSuccess
          ? `Downloaded ${exportSuccess}. You can adjust and download again or try new document.`
          : pageCount > PDF_WATERMARK_MAX_PAGES
            ? `${HINT} First ${PDF_WATERMARK_MAX_PAGES} pages only.`
            : HINT;

  const toolbarDisabled = !previewUrl || loadingPreview || exporting;

  const leftContent = (
    <div className="ppn-panel">
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="ppn-file-input"
        onChange={onInputChange}
      />
      <div className="ppn-sticky-top">
        <DocumentFileSourceCard
          fileName={fileName}
          fileSizeLabel={fileSizeLabel}
          pageCount={pageCount}
          acceptLabel="PDF"
          kind="pdf"
          hint="Supported formats: PDF"
          loading={loadingPreview && !exporting}
          error={error}
          onUploadClick={openPicker}
          onFiles={onDropFiles}
          disabled={loadingPreview || exporting}
          uploadProgress={uploadProgress}
          hideFilledMeta={Boolean(previewUrl && uploadProgress == null)}
        />
        {previewUrl && uploadProgress == null ? (
          <ToolPrepSourceCards
            compareOnly
            selectedKey={compareMode}
            onSelect={setCompareMode}
            originalImageUrl={previewUrl}
            previewImageUrl={previewUrl}
            watermark={previewWatermark}
            cutoutPending={loadingPage}
            sourceName={fileName}
            sourceSizeLabel={fileSizeLabel}
            sourceMetaExtra={
              pageCount > 0
                ? `${pageCount} page${pageCount === 1 ? "" : "s"}`
                : null
            }
            onUploadClick={openPicker}
            onRemove={handleClear}
            actionsDisabled={loadingPreview || exporting}
          />
        ) : null}
      </div>
      <div className="ppn-controls tool-prep-scroll" ref={scrollBodyRef}>
        <PageNumberControls
          settings={pageNumbers}
          onChange={setPageNumbers}
          disabled={toolbarDisabled}
          samplePage={currentPage || 1}
          samplePageCount={pageCount || 1}
        />
      </div>
    </div>
  );

  return (
    <WatermarkToolbarOverlayProvider leftPanelSelector='.tool-prep-popup[data-tool="pdf_page_numbers"] .tool-prep-popup__left'>
      <ToolPrepPopup
        toolKey="pdf_page_numbers"
        title="PDF Page Numbers"
        left={leftContent}
        bodyOwnsScroll
        busy={exporting}
        hasSource={Boolean(hasFile || loadingPreview || previewUrl)}
        preview={
          <div className="ppn-preview">
            <div className="ppn-preview__main ppn-preview-shell">
              <WatermarkTextFormatToolbar
                watermark={previewWatermark}
                onChange={mergeFromWatermark}
                visible={Boolean(previewUrl)}
                disabled={toolbarDisabled}
                anchorSelector='.tool-prep-popup[data-tool="pdf_page_numbers"] .tool-prep-popup__right'
              />
              <WatermarkPreview
                imageUrl={previewUrl}
                loading={loadingPreview || loadingPage}
                loadingLabel={
                  loadingPreview
                    ? "Rendering PDF…"
                    : `Loading page ${currentPage}…`
                }
                previewBg={TOOL_PREP_PREVIEW_BG.ORIGINAL}
                watermark={previewWatermark}
                onWatermarkChange={mergeFromWatermark}
                compareMode={compareMode}
                emptyLabel={
                  uploadProgress != null && !previewUrl
                    ? TOOL_PREP_PREPARING_PREVIEW_LABEL
                    : undefined
                }
                stageInset={
                  previewUrl
                    ? DOCUMENT_PREP_STAGE_INSET
                    : TOOL_PREP_STAGE_INSET
                }
              />
            </div>
            <DocumentPageThumbnails
              pageCount={pageCount}
              pageUrls={pageUrls}
              currentPage={currentPage}
              onSelect={goToPage}
              ensurePageUrl={ensurePageUrl}
              disabled={exporting}
              maxPages={PDF_WATERMARK_MAX_PAGES}
              getPageWatermark={(n) =>
                pageNumberSettingsToWatermarkPreview(
                  pageNumbers,
                  n,
                  pageCount || 1
                )
              }
              showMarks={compareMode !== "original"}
            />
          </div>
        }
        onDone={handleDone}
        onCancel={onCancel}
        confirmClose={Boolean(hasFile)}
        doneDisabled={
          !hasFile || loadingPreview || !previewUrl || !(pdfFile || file)
        }
        doneLabel="Download PDF"
        onSecondaryAction={exportSuccess ? onCancel : null}
        secondaryActionLabel="Back to Dashboard"
        footerHint={progressHint}
        footerHintClassName={
          exportError
            ? "ppn-footer-error"
            : exportSuccess
              ? "ppn-footer-success"
              : ""
        }
      />
      <WatermarkLeftPanelOverlays
        settings={previewWatermark}
        onChange={mergeFromWatermark}
        disabled={toolbarDisabled}
        positionStageSelector=".transform-preview__canvas"
      />
    </WatermarkToolbarOverlayProvider>
  );
};

export default PdfPageNumbersToolPrep;
